import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ETICHETE_OPERATOR,
  coloanaMeta,
  etichetaValoare,
  idFiltru,
  operatoriPentru,
  valoriDistincte,
  type FiltruRaport,
  type OperatorFiltru,
} from "@/utils/targetareRaport";
import type { ColoanaTabel, RandRaport } from "@/utils/targetareTabel";

type DialogFiltruProps<R extends RandRaport> = {
  deschis: boolean;
  coloane: ColoanaTabel<R>[];
  initial: FiltruRaport | null;
  randuri: R[];
  onInchide: () => void;
  onAplica: (filtru: FiltruRaport) => void;
};

export function DialogFiltru<R extends RandRaport>({
  deschis,
  coloane,
  initial,
  randuri,
  onInchide,
  onAplica,
}: DialogFiltruProps<R>) {
  const [coloana, setColoana] = useState<string>(
    initial?.coloana ?? coloane[0]?.id ?? ""
  );
  const [operator, setOperator] = useState<OperatorFiltru>(
    initial?.operator ?? "contine"
  );
  const [valoare, setValoare] = useState(initial?.valoare ?? "");
  const [valoare2, setValoare2] = useState(initial?.valoare2 ?? "");
  const [valori, setValori] = useState<string[]>(initial?.valori ?? []);
  const [cautareValori, setCautareValori] = useState("");
  const meta = coloanaMeta(coloane, coloana);
  const operatori = meta ? operatoriPentru(meta) : [];
  const optiuni =
    meta &&
    (meta.tip === "lista" || (meta.tip === "multi" && operator !== "contine"))
      ? (meta.optiuni?.slice() ?? valoriDistincte(randuri, coloana))
      : [];
  const areValoare = !["este_gol", "nu_este_gol"].includes(operator);

  const schimbaColoana = (id: string) => {
    setColoana(id);
    const m = coloanaMeta(coloane, id);
    const ops = m ? operatoriPentru(m) : [];
    setOperator(ops[0] ?? "contine");
    setValoare("");
    setValoare2("");
    setValori([]);
  };

  const aplica = () => {
    onAplica({
      id: initial?.id ?? idFiltru(),
      coloana,
      operator,
      valoare: areValoare && operator !== "in" ? valoare : undefined,
      valoare2: operator === "intre" ? valoare2 : undefined,
      valori: operator === "in" ? valori : undefined,
      activ: initial?.activ ?? true,
    });
  };
  const valid =
    !areValoare ||
    (operator === "in"
      ? valori.length > 0
      : valoare.trim().length > 0 &&
        (operator !== "intre" || valoare2.trim().length > 0));

  return (
    <Dialog open={deschis} onOpenChange={(o) => !o && onInchide()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {initial ? "Modifică filtrul" : "Filtru nou"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Coloană</p>
              <Select value={coloana} onValueChange={schimbaColoana}>
                <SelectTrigger aria-label="Coloană">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {coloane.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.eticheta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Operator</p>
              <Select
                value={operator}
                onValueChange={(v) => setOperator(v as OperatorFiltru)}
              >
                <SelectTrigger aria-label="Operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operatori.map((o) => (
                    <SelectItem key={o} value={o}>
                      {ETICHETE_OPERATOR[o]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {areValoare && operator === "in" && (
            <div>
              <Input
                value={cautareValori}
                onChange={(e) => setCautareValori(e.target.value)}
                placeholder="caută în valori"
                className="mb-1 h-8"
                aria-label="Caută valori"
              />
              <ul className="max-h-56 overflow-y-auto rounded border p-1 text-sm">
                {(optiuni.length > 0
                  ? optiuni
                  : valoriDistincte(randuri, coloana)
                )
                  .filter((o) =>
                    o.toLowerCase().includes(cautareValori.toLowerCase())
                  )
                  .map((o) => (
                    <li key={o}>
                      <label className="flex cursor-pointer items-center gap-2 px-1 py-0.5 hover:bg-muted">
                        <Checkbox
                          checked={valori.includes(o)}
                          onCheckedChange={(v) =>
                            setValori(
                              v === true
                                ? [...valori, o]
                                : valori.filter((x) => x !== o)
                            )
                          }
                        />
                        {etichetaValoare(coloane, coloana, o)}
                      </label>
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {areValoare && operator !== "in" && meta?.tip === "bool" && (
            <Select value={valoare || "da"} onValueChange={setValoare}>
              <SelectTrigger aria-label="Valoare">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="da">da</SelectItem>
                <SelectItem value="nu">nu</SelectItem>
              </SelectContent>
            </Select>
          )}
          {areValoare && operator !== "in" && meta?.tip === "lista" && (
            <Select value={valoare} onValueChange={setValoare}>
              <SelectTrigger aria-label="Valoare">
                <SelectValue placeholder="alege valoarea" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {optiuni.map((o) => (
                  <SelectItem key={o} value={o}>
                    {etichetaValoare(coloane, coloana, o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {areValoare &&
            operator !== "in" &&
            meta?.tip !== "bool" &&
            meta?.tip !== "lista" && (
              <div className="flex gap-2">
                <Input
                  type={meta?.tip === "numar" ? "number" : "text"}
                  value={valoare}
                  onChange={(e) => setValoare(e.target.value)}
                  placeholder={operator === "intre" ? "de la" : "valoare"}
                  aria-label="Valoare"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && valid) aplica();
                  }}
                />
                {operator === "intre" && (
                  <Input
                    type="number"
                    value={valoare2}
                    onChange={(e) => setValoare2(e.target.value)}
                    placeholder="până la"
                    aria-label="Valoare până la"
                  />
                )}
              </div>
            )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onInchide}>
            Anulează
          </Button>
          <Button onClick={aplica} disabled={!valid}>
            Aplică
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
